import { StandardEvents } from '@shopify/events';

/*
 * On a plain PDP landing, select the colour attached to Printify's first
 * product media. The native variant picker performs the section update, so
 * price, gallery and availability stay synchronized. The synthetic event is
 * intentionally not trusted: bmp-size-required.js therefore keeps Size empty.
 *
 * Explicit variant and option-values links (for example Meta ads) always win.
 *
 * bmp-globals.liquid temporarily hides the PDP product-information section on
 * a plain landing. This module ALWAYS releases that guard in a finally block,
 * including no-op/error paths, so a failed synchronization can never leave the
 * product hidden.
 */

const params = new URLSearchParams(window.location.search);
const isPlainLanding =
  !params.has('variant') && !params.has('option_values');

if (isPlainLanding) {
  initPrimaryColor()
    .catch((error) => {
      console.warn(
        '[bmp-primary-color] Primary colour initialization failed:',
        error
      );
    })
    .finally(revealProductInformation);
} else {
  revealProductInformation();
}

async function initPrimaryColor() {
  await customElements.whenDefined('variant-picker');

  const picker = document.querySelector(
    'variant-picker[data-template-product-match="true"]'
  );

  if (!(picker instanceof HTMLElement)) return;

  const marked = picker.querySelector(
    '[data-bmp-primary-media-match="true"]'
  );

  if (
    !(
      marked instanceof HTMLInputElement ||
      marked instanceof HTMLOptionElement
    )
  ) {
    return;
  }

  if (marked instanceof HTMLInputElement && marked.checked) return;
  if (marked instanceof HTMLOptionElement && marked.selected) return;

  let updatePromise;

  const captureUpdate = (event) => {
    if (event.target === picker && event.promise?.then) {
      updatePromise = event.promise;
    }
  };

  picker.addEventListener(
    StandardEvents.productSelect,
    captureUpdate,
    { once: true }
  );

  if (marked instanceof HTMLInputElement) {
    marked.click();
  } else {
    const select = marked.closest('select');

    if (!(select instanceof HTMLSelectElement)) return;

    select.value = marked.value;
    select.dispatchEvent(
      new Event('change', { bubbles: true })
    );
  }

  if (updatePromise?.then) {
    try {
      await updatePromise;
    } catch (error) {
      if (error?.name !== 'AbortError') throw error;
      return;
    }
  }

  await new Promise((resolve) =>
    requestAnimationFrame(resolve)
  );

  const cleanUrl = new URL(window.location.href);

  if (cleanUrl.searchParams.has('variant')) {
    cleanUrl.searchParams.delete('variant');
    history.replaceState({}, '', cleanUrl.toString());
  }
}

function revealProductInformation() {
  requestAnimationFrame(() => {
    document.documentElement.classList.remove(
      'bmp-primary-color-pending'
    );
  });
}